/**
 * Utility functions for array operations: get, set, and unique filtering.
 */

/**
 * Get unique items from array2 that do not exist in array1.
 * @param array1 - The first array.
 * @param array2 - The second array.
 * @param key - The key to compare uniqueness (for objects), leave undefined for primitives.
 * @returns Array of unique items.
 */
export const getUniqueItems = <T, K extends keyof T>(array1: T[], array2: T[], key?: K): T[] => {
  if (key) {
    const set = new Set(array1.map((item) => item[key]));
    return array2.filter((item) => !set.has(item[key]));
  }
  return array2.filter((item) => !array1.includes(item as any));
};

/**
 * Set (merge) unique items into an array without duplicates.
 * @param array - The original array.
 * @param newItems - The new items to merge.
 * @param key - The key to compare uniqueness (for objects), leave undefined for primitives.
 * @returns New array with merged unique items.
 */
export const setUniqueItems = <T, K extends keyof T>(array: T[], newItems: T[], key?: K): T[] => {
  if (key) {
    const map = new Map(array.map((item) => [item[key], item]));
    newItems.forEach((item) => map.set(item[key], item));
    return Array.from(map.values());
  }
  return Array.from(new Set([...array, ...newItems])) as T[];
};

/**
 * Check if two arrays contain common elements.
 * @param array1 - The first array.
 * @param array2 - The second array.
 * @param key - The key to compare (for objects), leave undefined for primitives.
 * @returns `true` if at least one common element exists, otherwise `false`.
 */
export const hasSameArray = <T, K extends keyof T | undefined>(
  array1: T[],
  array2: T[],
  key?: K,
): boolean => {
  if (array1.length !== array2.length) return false;

  if (key) {
    const set = new Set(array1.map((item) => item[key]));
    return array2.every((item) => set.has(item[key]));
  }
  return array1.every((item) => array2?.includes(item as any));
};

export const chunkArray = <T>(array: T[] | undefined, size: number): T[][] => {
  return Array.from(
    { length: Math.ceil((array?.length ?? 0) / size) },
    (_, i) => array?.slice(i * size, i * size + size) || [],
  );
};

export function findLastIndex<T>(
  array: T[],
  predicate: (value: T, index: number, array: T[]) => boolean,
  thisArg?: any,
): number {
  for (let i = array.length - 1; i >= 0; i--) {
    if (predicate.call(thisArg, array[i], i, array)) {
      return i;
    }
  }

  return -1;
}
